import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, BookOpen, Check, ChevronsUpDown, FileText, X, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCourses = () => {
  const { token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showInactive, setShowInactive] = useState(true);

  // Lookup data
  const [facultyList, setFacultyList] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);

  // Popover state
  const [facultyPopoverOpen, setFacultyPopoverOpen] = useState(false);
  const [subjectsPopoverOpen, setSubjectsPopoverOpen] = useState(false);
  const [subjectInput, setSubjectInput] = useState('');

  // Syllabus & Materials for edit mode
  const [courseSyllabus, setCourseSyllabus] = useState([]);
  const [courseMaterials, setCourseMaterials] = useState([]);

  // Syllabus editor state
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [syllabusEditor, setSyllabusEditor] = useState({ file_url: '', chapters: [] });
  const [syllabusFile, setSyllabusFile] = useState(null);
  const [syllabusUploading, setSyllabusUploading] = useState(false);

  // Material state
  const [materialForm, setMaterialForm] = useState({ title: '', file_url: '', subject: '', description: '' });
  const [materialFile, setMaterialFile] = useState(null);
  const [materialUploading, setMaterialUploading] = useState(false);

  // Active tab in dialog
  const [activeTab, setActiveTab] = useState('details');

  // Image upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultFormData = {
    name: '', category: 'JEE Mains', course_type: 'Online', duration: '',
    start_date: '', end_date: '', fee: 0, discounted_fee: 0,
    scholarship_available: false, seats_available: 0, total_seats: 0,
    description: '', subjects: [], faculty_ids: [], study_materials: [],
    test_series_included: false, batch_timing: '', mode_of_classes: '',
    image_url: '', is_featured: false, is_active: true
  };

  const [formData, setFormData] = useState(defaultFormData);

  const fetchCourses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/courses/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error:', error);
      // Fallback to public endpoint
      try {
        const response = await axios.get(`${API_URL}/api/courses`);
        setCourses(response.data);
      } catch (err) {
        console.error('Fallback error:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCourses();
    fetchFacultyAndSubjects();
  }, [fetchCourses]);

  const fetchFacultyAndSubjects = async () => {
    try {
      const [facRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/faculty`),
        axios.get(`${API_URL}/api/subjects`)
      ]);
      setFacultyList(facRes.data);
      // Combine subjects from subjects collection + faculty specializations
      const subjectNames = subRes.data.map(s => s.name);
      const facSubjects = facRes.data.flatMap(f => f.subject_specialization || []);
      const uniqueSubjects = [...new Set([...subjectNames, ...facSubjects])].sort();
      setSubjectsList(uniqueSubjects);
    } catch (error) {
      console.error('Error fetching lookup data:', error);
    }
  };

  const fetchCourseSyllabusAndMaterials = async (courseId) => {
    try {
      const [sylRes, matRes] = await Promise.all([
        axios.get(`${API_URL}/api/syllabus?course_id=${courseId}`),
        axios.get(`${API_URL}/api/study-materials?course_id=${courseId}`)
      ]);
      setCourseSyllabus(sylRes.data);
      setCourseMaterials(matRes.data);
    } catch (error) {
      console.error('Error fetching syllabus/materials:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let submitData = { ...formData };

      // Upload image first if a new file was selected
      if (imageFile) {
        const uploadForm = new FormData();
        uploadForm.append('file', imageFile);
        const uploadRes = await axios.post(
          `${API_URL}/api/uploads?file_type=course`,
          uploadForm,
          { headers }
        );
        submitData = { ...submitData, image_url: uploadRes.data.file_url };
      }

      if (editingCourse) {
        await axios.put(`${API_URL}/api/courses/${editingCourse.id}`, submitData, { headers });
        toast.success('Course updated successfully!');
      } else {
        // Exclude is_active on create — backend sets it to true
        const { is_active, ...createData } = submitData;
        await axios.post(`${API_URL}/api/courses`, createData, { headers });
        toast.success('Course created successfully!');
      }
      setDialogOpen(false);
      resetForm();
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this course? It will be hidden from students.')) return;
    try {
      await axios.delete(`${API_URL}/api/courses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course deactivated successfully!');
      fetchCourses();
    } catch (error) {
      toast.error('Failed to deactivate course');
    }
  };

  const openEditDialog = (course) => {
    setEditingCourse(course);
    setFormData({
      ...defaultFormData,
      ...course,
      faculty_ids: course.faculty_ids || [],
      subjects: course.subjects || [],
      study_materials: course.study_materials || [],
      test_series_included: course.test_series_included || false,
      mode_of_classes: course.mode_of_classes || '',
      image_url: course.image_url || '',
      is_active: course.is_active !== undefined ? course.is_active : true,
    });
    setActiveTab('details');
    setImageFile(null);
    setImagePreview(null);
    fetchCourseSyllabusAndMaterials(course.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCourse(null);
    setFormData(defaultFormData);
    setCourseSyllabus([]);
    setCourseMaterials([]);
    setSelectedSubject(null);
    setSyllabusEditor({ file_url: '', chapters: [] });
    setMaterialForm({ title: '', file_url: '', subject: '', description: '' });
    setActiveTab('details');
    setImageFile(null);
    setImagePreview(null);
    setSyllabusFile(null);
    setMaterialFile(null);
  };

  // Subject helpers
  const addSubject = (subject) => {
    if (subject && !formData.subjects.includes(subject)) {
      setFormData({ ...formData, subjects: [...formData.subjects, subject] });
    }
  };

  const removeSubject = (subject) => {
    setFormData({ ...formData, subjects: formData.subjects.filter(s => s !== subject) });
  };

  const addCustomSubject = () => {
    if (subjectInput.trim() && !formData.subjects.includes(subjectInput.trim())) {
      setFormData({ ...formData, subjects: [...formData.subjects, subjectInput.trim()] });
      setSubjectInput('');
    }
  };

  // Faculty helpers
  const toggleFaculty = (facultyId) => {
    const ids = formData.faculty_ids;
    if (ids.includes(facultyId)) {
      setFormData({ ...formData, faculty_ids: ids.filter(id => id !== facultyId) });
    } else {
      setFormData({ ...formData, faculty_ids: [...ids, facultyId] });
    }
  };

  // Syllabus editor helpers
  const selectSubjectForEdit = (subject) => {
    setSelectedSubject(subject);
    const existing = courseSyllabus.find(s => s.subject === subject);
    setSyllabusEditor({
      file_url: existing?.file_url || '',
      chapters: existing?.chapters || []
    });
    setSyllabusFile(null);
  };

  const addChapter = () => {
    setSyllabusEditor(prev => ({ ...prev, chapters: [...prev.chapters, { name: '', topics: [] }] }));
  };

  const updateChapterName = (idx, name) => {
    setSyllabusEditor(prev => ({
      ...prev,
      chapters: prev.chapters.map((c, i) => i === idx ? { ...c, name } : c)
    }));
  };

  const removeChapter = (idx) => {
    setSyllabusEditor(prev => ({ ...prev, chapters: prev.chapters.filter((_, i) => i !== idx) }));
  };

  const addTopic = (chapterIdx) => {
    setSyllabusEditor(prev => ({
      ...prev,
      chapters: prev.chapters.map((c, i) => i === chapterIdx ? { ...c, topics: [...c.topics, ''] } : c)
    }));
  };

  const updateTopic = (chapterIdx, topicIdx, value) => {
    setSyllabusEditor(prev => ({
      ...prev,
      chapters: prev.chapters.map((c, i) =>
        i === chapterIdx ? { ...c, topics: c.topics.map((t, j) => j === topicIdx ? value : t) } : c
      )
    }));
  };

  const removeTopic = (chapterIdx, topicIdx) => {
    setSyllabusEditor(prev => ({
      ...prev,
      chapters: prev.chapters.map((c, i) =>
        i === chapterIdx ? { ...c, topics: c.topics.filter((_, j) => j !== topicIdx) } : c
      )
    }));
  };

  const saveSyllabus = async () => {
    if (!selectedSubject || !editingCourse) return;
    const headers = { Authorization: `Bearer ${token}` };
    let file_url = syllabusEditor.file_url || null;
    try {
      setSyllabusUploading(true);
      if (syllabusFile) {
        const uploadForm = new FormData();
        uploadForm.append('file', syllabusFile);
        const uploadRes = await axios.post(`${API_URL}/api/uploads?file_type=study_material`, uploadForm, { headers });
        file_url = uploadRes.data.file_url;
      }
      await axios.post(`${API_URL}/api/syllabus`, {
        course_id: editingCourse.id,
        subject: selectedSubject,
        file_url,
        chapters: syllabusEditor.chapters.filter(c => c.name.trim())
      }, { headers });
      toast.success('Syllabus saved!');
      setSyllabusFile(null);
      fetchCourseSyllabusAndMaterials(editingCourse.id);
    } catch (error) {
      toast.error('Failed to save syllabus');
    } finally {
      setSyllabusUploading(false);
    }
  };

  const clearSubjectSyllabus = async (subject) => {
    const existing = courseSyllabus.find(s => s.subject === subject);
    if (!existing) return;
    if (!window.confirm(`Clear syllabus for ${subject}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/syllabus/${existing.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Syllabus cleared');
      if (selectedSubject === subject) {
        setSyllabusEditor({ file_url: '', chapters: [] });
        setSyllabusFile(null);
      }
      fetchCourseSyllabusAndMaterials(editingCourse.id);
    } catch (error) {
      toast.error('Failed to clear syllabus');
    }
  };

  // Study Material CRUD
  const addMaterial = async () => {
    if (!materialForm.title || (!materialForm.file_url && !materialFile)) return;
    const headers = { Authorization: `Bearer ${token}` };
    let file_url = materialForm.file_url;
    try {
      if (materialFile) {
        setMaterialUploading(true);
        const uploadForm = new FormData();
        uploadForm.append('file', materialFile);
        const uploadRes = await axios.post(
          `${API_URL}/api/uploads?file_type=study_material`,
          uploadForm,
          { headers }
        );
        file_url = uploadRes.data.file_url.replace(/^https?:\/\/[^/]+/, '');
      }
      await axios.post(`${API_URL}/api/study-materials`, {
        ...materialForm,
        file_url,
        course_id: editingCourse.id
      }, { headers });
      toast.success('Study material added!');
      setMaterialForm({ title: '', file_url: '', subject: '', description: '' });
      setMaterialFile(null);
      fetchCourseSyllabusAndMaterials(editingCourse.id);
    } catch (error) {
      toast.error('Failed to add study material');
    } finally {
      setMaterialUploading(false);
    }
  };

  const deleteMaterial = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/study-materials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Study material deleted!');
      fetchCourseSyllabusAndMaterials(editingCourse.id);
    } catch (error) {
      toast.error('Failed to delete study material');
    }
  };

  const displayedCourses = showInactive ? courses : courses.filter(c => c.is_active !== false);

  return (
    <AdminLayout title="Courses Management">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <p className="text-muted-foreground">Manage all courses offered by the institute.</p>
          <div className="flex items-center gap-2">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} />
            <Label className="text-sm">Show inactive</Label>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-course-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Course
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCourse ? 'Edit Course' : 'Add New Course'}</DialogTitle>
            </DialogHeader>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full">
                <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
                {editingCourse && <TabsTrigger value="syllabus" className="flex-1">Syllabus</TabsTrigger>}
                {editingCourse && <TabsTrigger value="materials" className="flex-1">Study Materials</TabsTrigger>}
              </TabsList>

              {/* ===== DETAILS TAB ===== */}
              <TabsContent value="details">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Row 1: Name + Category */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Course Name</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="course-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['JEE Mains', 'JEE Advanced', 'NEET UG', 'NEET PG', 'Foundation'].map(c => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 2: Course Type + Mode of Classes */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Course Type</Label>
                      <Select value={formData.course_type} onValueChange={(v) => setFormData({ ...formData, course_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Online">Online</SelectItem>
                          <SelectItem value="Offline">Offline</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Mode of Classes</Label>
                      <Select value={formData.mode_of_classes} onValueChange={(v) => setFormData({ ...formData, mode_of_classes: v })}>
                        <SelectTrigger><SelectValue placeholder="Select mode" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Live">Live</SelectItem>
                          <SelectItem value="Recorded">Recorded</SelectItem>
                          <SelectItem value="Hybrid">Hybrid</SelectItem>
                          <SelectItem value="In-Person">In-Person</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 3: Duration + Batch Timing */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Duration</Label>
                      <Input value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} placeholder="e.g., 12 Months" />
                    </div>
                    <div className="space-y-2">
                      <Label>Batch Timing</Label>
                      <Input value={formData.batch_timing} onChange={(e) => setFormData({ ...formData, batch_timing: e.target.value })} placeholder="e.g., 9AM - 1PM" />
                    </div>
                  </div>

                  {/* Row 4: Start Date + End Date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} />
                    </div>
                  </div>

                  {/* Row 5: Fee + Discounted Fee */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fee (₹)</Label>
                      <Input type="number" value={formData.fee} onChange={(e) => setFormData({ ...formData, fee: Number(e.target.value) })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Discounted Fee (₹)</Label>
                      <Input type="number" value={formData.discounted_fee} onChange={(e) => setFormData({ ...formData, discounted_fee: Number(e.target.value) })} />
                    </div>
                  </div>

                  {/* Row 6: Total Seats + Available Seats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Total Seats</Label>
                      <Input type="number" value={formData.total_seats} onChange={(e) => setFormData({ ...formData, total_seats: Number(e.target.value) })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Available Seats</Label>
                      <Input type="number" value={formData.seats_available} onChange={(e) => setFormData({ ...formData, seats_available: Number(e.target.value) })} />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
                  </div>

                  {/* Course Image Upload */}
                  <div className="space-y-2">
                    <Label>Course Image</Label>
                    <div
                      className="border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 transition-colors overflow-hidden"
                      onClick={() => document.getElementById('course-image-input').click()}
                    >
                      {imagePreview || formData.image_url ? (
                        <div className="relative">
                          <img
                            src={imagePreview || formData.image_url}
                            alt="Course preview"
                            className="w-full h-40 object-cover"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setImageFile(null);
                              setImagePreview(null);
                              setFormData({ ...formData, image_url: '' });
                            }}
                            className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          {imagePreview && (
                            <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                              {imageFile?.name}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                          <Upload className="w-8 h-8" />
                          <span className="text-sm font-medium">Click to upload image</span>
                          <span className="text-xs">JPG, PNG, GIF supported</span>
                        </div>
                      )}
                    </div>
                    <input
                      id="course-image-input"
                      type="file"
                      accept="image/jpeg,image/png,image/gif"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setImageFile(file);
                          setImagePreview(URL.createObjectURL(file));
                        }
                        e.target.value = '';
                      }}
                    />
                  </div>

                  <Separator />

                  {/* Subjects — Searchable multi-select with custom input */}
                  <div className="space-y-2">
                    <Label>Subjects</Label>
                    <div className="flex gap-2">
                      <Popover open={subjectsPopoverOpen} onOpenChange={setSubjectsPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" type="button" className="justify-between w-full">
                            Select subjects...
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search subjects..." />
                            <CommandList>
                              <CommandEmpty>No subject found.</CommandEmpty>
                              <CommandGroup>
                                {subjectsList.map(subject => (
                                  <CommandItem key={subject} onSelect={() => addSubject(subject)}>
                                    <Check className={`mr-2 h-4 w-4 ${formData.subjects.includes(subject) ? 'opacity-100' : 'opacity-0'}`} />
                                    {subject}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <Input
                        value={subjectInput}
                        onChange={(e) => setSubjectInput(e.target.value)}
                        placeholder="Custom subject"
                        className="w-48"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomSubject(); } }}
                      />
                      <Button type="button" variant="outline" onClick={addCustomSubject}>Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.subjects.map((s, i) => (
                        <Badge key={i} variant="secondary" className="cursor-pointer gap-1" onClick={() => removeSubject(s)}>
                          {s} <X className="h-3 w-3" />
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Faculty — Searchable multi-select */}
                  <div className="space-y-2">
                    <Label>Faculty</Label>
                    <Popover open={facultyPopoverOpen} onOpenChange={setFacultyPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" type="button" className="justify-between w-full">
                          {formData.faculty_ids.length > 0
                            ? `${formData.faculty_ids.length} faculty selected`
                            : 'Select faculty...'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search faculty..." />
                          <CommandList>
                            <CommandEmpty>No faculty found.</CommandEmpty>
                            <CommandGroup>
                              {facultyList.map(f => (
                                <CommandItem key={f.id} onSelect={() => toggleFaculty(f.id)}>
                                  <Check className={`mr-2 h-4 w-4 ${formData.faculty_ids.includes(f.id) ? 'opacity-100' : 'opacity-0'}`} />
                                  <div>
                                    <div className="font-medium">{f.name}</div>
                                    <div className="text-xs text-muted-foreground">{f.subject_specialization?.join(', ')}</div>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.faculty_ids.map(id => {
                        const fac = facultyList.find(f => f.id === id);
                        return fac ? (
                          <Badge key={id} variant="secondary" className="cursor-pointer gap-1" onClick={() => toggleFaculty(id)}>
                            {fac.name} <X className="h-3 w-3" />
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>

                  <Separator />

                  {/* Toggles row */}
                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Switch checked={formData.scholarship_available} onCheckedChange={(c) => setFormData({ ...formData, scholarship_available: c })} />
                      <Label>Scholarship Available</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={formData.test_series_included} onCheckedChange={(c) => setFormData({ ...formData, test_series_included: c })} />
                      <Label>Test Series Included</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={formData.is_featured} onCheckedChange={(c) => setFormData({ ...formData, is_featured: c })} />
                      <Label>Featured</Label>
                    </div>
                    {editingCourse && (
                      <div className="flex items-center gap-2">
                        <Switch checked={formData.is_active} onCheckedChange={(c) => setFormData({ ...formData, is_active: c })} />
                        <Label>Active</Label>
                      </div>
                    )}
                  </div>

                  <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="save-course-btn">
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                        {imageFile ? 'Uploading image...' : 'Saving...'}
                      </span>
                    ) : (
                      editingCourse ? 'Update Course' : 'Create Course'
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* ===== SYLLABUS TAB ===== */}
              {editingCourse && (
                <TabsContent value="syllabus">
                  <div className="flex gap-4 h-[480px]">
                    {/* Subject List */}
                    <div className="w-48 shrink-0 border-r pr-3 space-y-1 overflow-y-auto">
                      <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Subjects</p>
                      {(editingCourse.subjects || []).length === 0 ? (
                        <p className="text-xs text-muted-foreground">No subjects added to this course.</p>
                      ) : (
                        (editingCourse.subjects || []).map(subject => {
                          const hasSyllabus = courseSyllabus.some(s => s.subject === subject && (s.chapters?.length > 0 || s.file_url));
                          return (
                            <button
                              key={subject}
                              type="button"
                              onClick={() => selectSubjectForEdit(subject)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between gap-1 ${
                                selectedSubject === subject
                                  ? 'bg-primary text-white'
                                  : 'hover:bg-muted text-foreground'
                              }`}
                            >
                              <span className="truncate">{subject}</span>
                              {hasSyllabus && (
                                <span className={`w-2 h-2 rounded-full shrink-0 ${selectedSubject === subject ? 'bg-white' : 'bg-green-500'}`} />
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>

                    {/* Editor Panel */}
                    <div className="flex-1 overflow-y-auto">
                      {!selectedSubject ? (
                        <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                          Select a subject to edit its syllabus
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold">{selectedSubject}</h4>
                            {courseSyllabus.some(s => s.subject === selectedSubject) && (
                              <Button type="button" variant="ghost" size="sm" className="text-destructive text-xs h-7" onClick={() => clearSubjectSyllabus(selectedSubject)}>
                                <Trash2 className="w-3 h-3 mr-1" /> Clear
                              </Button>
                            )}
                          </div>

                          {/* File Upload */}
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Subject PDF (optional)</Label>
                            <div className="flex items-center gap-2">
                              <label htmlFor="syllabus-file-input" className="flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-xs cursor-pointer hover:bg-muted shrink-0">
                                <Upload className="w-3 h-3" />
                                {syllabusFile ? syllabusFile.name : 'Upload PDF'}
                              </label>
                              <input id="syllabus-file-input" type="file" accept=".pdf,.doc,.docx" className="hidden"
                                onChange={(e) => { const f = e.target.files[0]; if (f) { setSyllabusFile(f); setSyllabusEditor(p => ({ ...p, file_url: '' })); } e.target.value = ''; }} />
                              {syllabusFile ? (
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setSyllabusFile(null)}>
                                  <X className="w-3 h-3" />
                                </Button>
                              ) : (
                                <Input placeholder="or paste URL" value={syllabusEditor.file_url}
                                  onChange={(e) => setSyllabusEditor(p => ({ ...p, file_url: e.target.value }))}
                                  className="h-8 text-xs flex-1" />
                              )}
                              {syllabusEditor.file_url && !syllabusFile && (
                                <a href={syllabusEditor.file_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline shrink-0">View</a>
                              )}
                            </div>
                          </div>

                          <Separator />

                          {/* Chapters */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs text-muted-foreground uppercase">Chapters</Label>
                              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addChapter}>
                                <Plus className="w-3 h-3 mr-1" /> Add Chapter
                              </Button>
                            </div>

                            {syllabusEditor.chapters.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-4">No chapters yet. Add a chapter or save with just the file.</p>
                            ) : (
                              syllabusEditor.chapters.map((chapter, ci) => (
                                <div key={ci} className="border rounded-lg p-3 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <Input
                                      placeholder={`Chapter ${ci + 1} name`}
                                      value={chapter.name}
                                      onChange={(e) => updateChapterName(ci, e.target.value)}
                                      className="h-8 text-sm font-medium flex-1"
                                    />
                                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => removeChapter(ci)}>
                                      <X className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                  <div className="pl-2 space-y-1.5">
                                    {chapter.topics.map((topic, ti) => (
                                      <div key={ti} className="flex items-center gap-1.5">
                                        <span className="text-muted-foreground text-xs">•</span>
                                        <Input
                                          placeholder={`Topic ${ti + 1}`}
                                          value={topic}
                                          onChange={(e) => updateTopic(ci, ti, e.target.value)}
                                          className="h-7 text-xs flex-1"
                                        />
                                        <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => removeTopic(ci, ti)}>
                                          <X className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    ))}
                                    <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground pl-2" onClick={() => addTopic(ci)}>
                                      <Plus className="w-3 h-3 mr-1" /> Add Topic
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>

                          <Button type="button" onClick={saveSyllabus} disabled={syllabusUploading} className="w-full">
                            {syllabusUploading ? (
                              <span className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                </svg>
                                Saving...
                              </span>
                            ) : 'Save Syllabus'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>
              )}

              {/* ===== STUDY MATERIALS TAB ===== */}
              {editingCourse && (
                <TabsContent value="materials">
                  <div className="space-y-4">
                    <div className="space-y-3 p-4 border rounded-lg">
                      <h4 className="font-medium">Add Study Material</h4>
                      <Input
                        placeholder="Title"
                        value={materialForm.title}
                        onChange={(e) => setMaterialForm({ ...materialForm, title: e.target.value })}
                      />
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">File</Label>
                        <div className="flex items-center gap-2">
                          <label
                            htmlFor="material-file-input"
                            className="flex items-center gap-1.5 px-3 py-2 border rounded-md text-sm cursor-pointer hover:bg-muted transition-colors shrink-0"
                          >
                            <Upload className="w-3.5 h-3.5" />
                            {materialFile ? materialFile.name : 'Upload file'}
                          </label>
                          <input
                            id="material-file-input"
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setMaterialFile(file);
                                setMaterialForm({ ...materialForm, file_url: '' });
                              }
                              e.target.value = '';
                            }}
                          />
                          <span className="text-xs text-muted-foreground shrink-0">or</span>
                          <Input
                            placeholder="Paste full URL"
                            value={materialForm.file_url}
                            disabled={!!materialFile}
                            onChange={(e) => setMaterialForm({ ...materialForm, file_url: e.target.value })}
                            className="flex-1"
                          />
                          {materialFile && (
                            <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => setMaterialFile(null)}>
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          placeholder="Subject (optional)"
                          value={materialForm.subject}
                          onChange={(e) => setMaterialForm({ ...materialForm, subject: e.target.value })}
                        />
                        <Input
                          placeholder="Description (optional)"
                          value={materialForm.description}
                          onChange={(e) => setMaterialForm({ ...materialForm, description: e.target.value })}
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={addMaterial}
                        size="sm"
                        disabled={materialUploading || !materialForm.title || (!materialForm.file_url && !materialFile)}
                      >
                        {materialUploading ? (
                          <span className="flex items-center gap-1.5">
                            <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                            Uploading...
                          </span>
                        ) : (
                          <><Plus className="w-4 h-4 mr-1" /> Add</>
                        )}
                      </Button>
                    </div>

                    <Separator />

                    {courseMaterials.length > 0 ? (
                      <div className="space-y-2">
                        {courseMaterials.map(m => (
                          <div key={m.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-muted-foreground" />
                              <div>
                                <p className="font-medium text-sm">{m.title}</p>
                                {m.subject && <Badge variant="outline" className="text-xs">{m.subject}</Badge>}
                                {m.description && <p className="text-xs text-muted-foreground">{m.description}</p>}
                                <a href={m.file_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">{m.file_url}</a>
                              </div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => deleteMaterial(m.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-sm text-muted-foreground py-8">No study materials yet.</p>
                    )}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      {/* Course List */}
      {loading ? (
        <div className="space-y-4">{[1, 2, 3].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-24"></CardContent></Card>)}</div>
      ) : displayedCourses.length > 0 ? (
        <div className="space-y-4">
          {displayedCourses.map((course) => (
            <Card key={course.id} data-testid={`course-item-${course.id}`} className={course.is_active === false ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                    {course.image_url
                      ? <img src={course.image_url.startsWith('http') ? course.image_url : `${API_URL}${course.image_url.startsWith('/') ? '' : '/'}${course.image_url}`} alt={course.name} className="w-full h-full object-cover" />
                      : <BookOpen className="w-8 h-8 text-primary" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold truncate">{course.name}</h4>
                      {course.is_featured && <Badge className="bg-secondary">Featured</Badge>}
                      {course.is_active === false && <Badge variant="destructive">Inactive</Badge>}
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                      <Badge variant="outline">{course.category}</Badge>
                      <span>•</span>
                      <span>{course.course_type}</span>
                      {course.mode_of_classes && <><span>•</span><span>{course.mode_of_classes}</span></>}
                      <span>•</span>
                      <span>₹{course.fee?.toLocaleString()}</span>
                      <span>•</span>
                      <span>{course.seats_available} seats left</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => openEditDialog(course)} data-testid={`edit-${course.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => handleDelete(course.id)} className="text-destructive" data-testid={`delete-${course.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="py-16 text-center"><BookOpen className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-heading text-xl font-semibold mb-2">No Courses Yet</h3><p className="text-muted-foreground">Create your first course to get started.</p></CardContent></Card>
      )}
    </AdminLayout>
  );
};

export default AdminCourses;
